import { BookOpen } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { AUDIENCES, GUIDES_INDEX_SEO, listGuides } from "@/content/guides";
import { GUIDE_AUTHORS, authorAnchor } from "@/content/guides/authors";
import { GuideCard } from "./GuidePage";
import { GuidesOnly } from "./GuidesOnly";

// /guides: the hub. Every English guide, grouped by who it's for (a guide for two audiences is listed under both),
// and the people who write them (the author anchors the guides' bylines link to).

const GuidesHub = () => {
  const guides = listGuides("en");
  return (
    <div className="min-h-screen bg-background">
      <AppHeader variant="marketing" />
      <main>
        <section className="relative overflow-hidden bg-hawk-navy pb-16 pt-32 text-white md:pb-20">
          <div className="absolute inset-0 bg-radial-orange opacity-20" aria-hidden="true" />
          <div className="container relative z-10 mx-auto max-w-5xl px-4">
            <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm font-medium">
              <BookOpen size={16} className="text-hawk-orange" aria-hidden="true" />
              Photohawk guides
            </span>
            <h1 className="mb-4 max-w-3xl font-display text-4xl font-bold md:text-5xl">Guides to event photos, from the people who host them</h1>
            <p className="max-w-2xl text-lg text-white/80">{GUIDES_INDEX_SEO.description}</p>
            <nav aria-label="Guide sections" className="mt-8 flex flex-wrap gap-2">
              {AUDIENCES.map((a) => (
                <a key={a.id} href={`#${a.id}`} className="inline-flex min-h-11 items-center rounded-full border border-white/25 px-4 text-sm font-semibold hover:bg-white/10">
                  {a.title}
                </a>
              ))}
            </nav>
          </div>
        </section>

        {AUDIENCES.map((a, i) => {
          const list = guides.filter((g) => g.audience.includes(a.id));
          if (!list.length) return null;
          return (
            <section key={a.id} id={a.id} aria-labelledby={`${a.id}-h`} className={`scroll-mt-24 py-14 md:py-16 ${i % 2 ? "bg-secondary/30" : ""}`}>
              <div className="container mx-auto max-w-5xl px-4">
                <h2 id={`${a.id}-h`} className="mb-1 font-display text-3xl font-bold">
                  {a.title}
                </h2>
                <p className="mb-8 text-muted-foreground">{a.blurb}</p>
                <ul className="grid list-none gap-6 p-0 md:grid-cols-2">
                  {list.map((g) => (
                    <li key={g.slug}>
                      <GuideCard guide={g} />
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          );
        })}

        <section aria-labelledby="authors-h" className="border-t border-border py-14 md:py-16">
          <div className="container mx-auto max-w-5xl px-4">
            <h2 id="authors-h" className="mb-2 font-display text-3xl font-bold">
              Who writes these guides
            </h2>
            <p className="mb-8 max-w-2xl text-muted-foreground">Every guide says who wrote it, who reviewed it and when it was last checked.</p>
            <ul className="grid list-none gap-6 p-0 md:grid-cols-2">
              {GUIDE_AUTHORS.map((au) => (
                <li key={au.id} id={authorAnchor(au.id)} className="scroll-mt-24 rounded-2xl border border-border bg-card p-6" data-testid={`author-${au.id}`}>
                  <p className="m-0 font-display text-xl font-bold">{au.name}</p>
                  <p className="mb-3 mt-0 text-sm text-muted-foreground">{au.role}</p>
                  <p className="m-0 text-sm leading-relaxed text-foreground/80">{au.bio}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const GuidesIndex = () => (
  <GuidesOnly>
    <GuidesHub />
  </GuidesOnly>
);

export default GuidesIndex;
